import { AxiosInstance } from 'axios';
import { createApiClient } from '../http/client';
import { ApiResponse } from '../../interfaces/common';
import { AgentDetail, CreateAgentRequest, UpdateAgentRequest } from '../../interfaces/calls';
import { AuthService } from '../auth';

const APPOINTMENTS_API_BASE_URL = process.env.REACT_APP_PUBLIC_APPOINTMENTS_API_BASE_URL;

class AgentsService {
  private axios: AxiosInstance;

  constructor() {
    this.axios = createApiClient({
      baseURL: APPOINTMENTS_API_BASE_URL,
    });
  }

  private getAuthHeaders() {
    return {
      Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
    };
  }

  async getAllAgents(): Promise<ApiResponse<AgentDetail[]>> {
    try {
      const response = await this.axios.get<AgentDetail[]>('/communications/agents-management', {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error fetching agents:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to fetch agents',
      };
    }
  }

  async getAgent(id: number): Promise<ApiResponse<AgentDetail>> {
    try {
      const response = await this.axios.get<AgentDetail>(`/communications/agents-management/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error fetching agent:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to fetch agent',
      };
    }
  }

  async createAgent(data: CreateAgentRequest): Promise<ApiResponse<AgentDetail>> {
    try {
      const response = await this.axios.post<AgentDetail>('/communications/agents-management', data, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 201,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error creating agent:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to create agent',
      };
    }
  }

  async updateAgent(id: number, data: UpdateAgentRequest): Promise<ApiResponse<AgentDetail>> {
    try {
      const response = await this.axios.put<AgentDetail>(`/communications/agents-management/${id}`, data, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error updating agent:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to update agent',
      };
    }
  }

  async deleteAgent(id: number): Promise<ApiResponse<void>> {
    try {
      await this.axios.delete(`/communications/agents-management/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
      };
    } catch (error: any) {
      console.error('Error deleting agent:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to delete agent',
      };
    }
  }
}

export default new AgentsService();
